import { Link } from "react-router-dom";
import { ArrowRight, Award, Quote, MapPin, GraduationCap, Stethoscope, BookOpen, Briefcase, User } from "lucide-react";
import Placeholder from "@/components/Placeholder";
import { credentials } from "@/lib/data";
import { Reveal, StaggerGroup, StaggerItem, GlowCard, AnimatedLine, FloatingBlob } from "@/components/Motion";
import SEO from "@/components/SEO";

export default function About() {
  return (
    <div data-testid="about-page" className="relative overflow-hidden">
      <SEO title="About Dr. Priyanka" description="Learn about Dr. Priyanka Gupta's 20+ years of experience in obstetrics and gynaecology." />
      <FloatingBlob color="aurora-purple" size={500} top="-10%" right="-10%" delay={0} />

      {/* Hero */}
      <section className="py-16 lg:py-24 relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="overline mb-4">About</div>
            <h1 className="font-heading text-5xl md:text-6xl text-[var(--color-text)] leading-[1.05] max-w-4xl">
              A Gold Medalist&apos;s commitment to <em className="text-[var(--color-coral)] not-italic">women&apos;s healthcare.</em>
            </h1>
          </Reveal>
        </div>
      </section>

      {/* Bio + Portrait */}
      <section className="pb-20 lg:pb-28 relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-5" delay={0.2}>
            <div className="img-hover-scale">
              <Placeholder variant="tall" label="Doctor Portrait" Icon={User} testId="about-main-portrait" src="/images/facilities/m2.webp" />
            </div>
            <div className="mt-6 grid grid-cols-2 gap-4">
              <div className="img-hover-scale"><Placeholder variant="square" label="Consultation" src="/images/facilities/DSC04279.webp" /></div>
              <div className="img-hover-scale"><Placeholder variant="square" label="With Patient" src="/images/facilities/DSC04307.webp" /></div>
            </div>
          </Reveal>
          
          <Reveal className="lg:col-span-7" delay={0.3}>
            <h2 className="font-heading text-3xl md:text-4xl text-[var(--color-text)] leading-tight">Dr. Priyanka Gupta</h2>
            <div className="text-sm text-[var(--color-text-muted)] font-semibold mt-2 mb-2 uppercase tracking-wide">MBBS · MD · Gold Medalist · Fellow Laparoscopy</div>
            <div className="text-sm text-[var(--color-coral)] font-semibold">Co-Founder &amp; Director — Obstetrics, Gynaecology &amp; Women&apos;s Health, WellMed Hospitals</div>

            <div className="mt-7 space-y-5 text-base md:text-lg text-[var(--color-text-muted)] leading-relaxed">
              <p>
                Dr. Priyanka Gupta is a highly respected Obstetrician and Gynaecologist with over 20 years of clinical excellence dedicated to comprehensive women&apos;s healthcare — from adolescence through menopause and beyond.
              </p>
              <p>
                A Gold Medalist in MD (Obstetrics &amp; Gynaecology) and a Fellow in Laparoscopic Surgery, she combines strong academic foundations with advanced surgical precision and a deeply compassionate, patient-first approach.
              </p>
              <p>
                Over the course of her distinguished career, Dr. Gupta has delivered more than <span className="text-[var(--color-coral)] font-semibold">20,000 babies</span> across Delhi, Vadodara, Bengaluru, and now Gurugram — earning enduring trust from thousands of families.
              </p>
              <p>
                She is widely recognized for her expertise in managing high-risk pregnancies, ensuring safe outcomes for both mother and baby through vigilant monitoring, evidence-based protocols, and modern birthing practices. She also offers advanced laparoscopic (minimally invasive) surgery for faster recovery and improved outcomes.
              </p>
              <p>
                In addition, Dr. Gupta provides comprehensive care in infertility and reproductive health, menstrual and hormonal disorders, and structured women&apos;s wellness programs designed to support long-term health and preventive care.
              </p>
            </div>

            <div className="mt-10 glass-card p-7 relative border-[var(--color-coral)]/30">
              <Quote className="absolute -top-3 -left-3 w-7 h-7 text-[var(--color-coral)] fill-[var(--color-coral)]" />
              <p className="font-heading italic text-xl md:text-2xl text-white leading-snug">
                &ldquo;The journey of motherhood is sacred and every woman deserves the highest standard of care delivered with empathy and respect.&rdquo;
              </p>
              <div className="mt-4 text-xs uppercase tracking-wider text-[var(--color-coral)] font-semibold">— Dr. Priyanka Gupta</div>
            </div>
          </Reveal>
        </div>
      </section>

      <AnimatedLine className="mx-6 lg:mx-16" />

      {/* Credentials */}
      <section className="py-20 lg:py-24 relative overflow-hidden bg-[var(--color-surface)]">
        <FloatingBlob color="aurora-warm" size={400} bottom="-10%" left="-5%" delay={2} />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
          <Reveal>
            <div className="overline mb-4">Credentials</div>
            <h2 className="font-heading text-4xl md:text-5xl text-[var(--color-text)] leading-tight max-w-3xl">Academic foundation. Surgical precision. Compassionate practice.</h2>
          </Reveal>
          
          <StaggerGroup className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-5" stagger={0.1}>
            {credentials.map((c, i) => {
              const icons = [GraduationCap, Award, Stethoscope, BookOpen];
              const I = icons[i] || GraduationCap;
              return (
                <StaggerItem key={c.label}>
                  <GlowCard className="p-7 h-full">
                    <I className="w-7 h-7 text-[var(--color-coral)] mb-4" strokeWidth={1.5} />
                    <div className="font-heading text-xl text-white">{c.label}</div>
                    <div className="mt-2 text-sm text-white/80">{c.detail}</div>
                  </GlowCard>
                </StaggerItem>
              );
            })}
          </StaggerGroup>
        </div>
      </section>

      <AnimatedLine className="mx-6 lg:mx-16" />

      {/* Career */}
      <section className="py-20 lg:py-28 relative">
        <FloatingBlob color="aurora-coral" size={300} top="20%" right="5%" delay={1} />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-start relative">
          <Reveal className="lg:col-span-5">
            <div className="overline mb-4">Career</div>
            <h2 className="font-heading text-4xl md:text-5xl text-[var(--color-text)] leading-tight">Cities entrusted, families served.</h2>
            <p className="mt-5 text-[var(--color-text-muted)] leading-relaxed">
              From senior consultancies in Delhi and Vadodara to leading the Obstetrics &amp; Gynaecology programme at WellMed Hospitals — every chapter has been built around one principle: the patient comes first.
            </p>
            <div className="mt-8 flex items-center gap-3 text-sm text-[var(--color-text-muted)]">
              <MapPin className="w-4 h-4 text-[var(--color-coral)]" /> Delhi · Vadodara · Bengaluru · Gurugram
            </div>
          </Reveal>
          
          <Reveal className="lg:col-span-7" delay={0.2}>
            <ol className="relative border-l border-[var(--color-border-2)] pl-8 space-y-9">
              {[
                { year: "Present", title: "Co-Founder & Director, WellMed Hospitals", desc: "Leading Obstetrics, Gynaecology & Women's Health at WellMed — Sector 39, Gurugram." },
                { year: "Earlier", title: "Senior Consultant — Gurugram & Delhi NCR", desc: "Practiced at leading multi-specialty hospitals; built deep referral networks." },
                { year: "Earlier", title: "Consultant Obstetrician & Gynaecologist", desc: "Practiced across Vadodara and Bengaluru, where she delivered thousands of babies and completed her laparoscopy fellowship." },
                { year: "Foundation", title: "MD (Ob & Gyn) — Gold Medalist", desc: "Awarded the Gold Medal at MS University of Baroda. Earlier MBBS from PGIMS Rohtak." },
              ].map((m) => (
                <li key={m.title} className="relative">
                  <span className="absolute -left-[37px] top-1 w-3 h-3 rounded-full bg-[var(--color-coral)] ring-4 ring-[var(--color-bg)]" />
                  <div className="text-xs uppercase tracking-[0.2em] text-[var(--color-coral)] font-semibold">{m.year}</div>
                  <div className="font-heading text-2xl text-[var(--color-text)] mt-1">{m.title}</div>
                  <p className="text-sm text-[var(--color-text-muted)] mt-2 leading-relaxed">{m.desc}</p>
                </li>
              ))}
            </ol>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-20 lg:pb-28">
        <Reveal>
          <div className="max-w-5xl mx-auto px-6 lg:px-8">
            <div className="glass-card rounded-3xl p-10 lg:p-14 text-center">
              <Briefcase className="w-9 h-9 text-[var(--color-coral)] mx-auto mb-4" strokeWidth={1.5} />
              <h2 className="font-heading text-3xl md:text-4xl text-white">Have questions before booking?</h2>
              <p className="mt-3 text-white/80 max-w-xl mx-auto">A private, unhurried consultation is the best way to begin. Book directly or speak with the clinic.</p>
              <div className="mt-7 flex flex-wrap justify-center gap-4">
                <Link to="/appointment" data-testid="about-cta-book" className="btn-primary">Book Appointment <ArrowRight className="w-4 h-4" /></Link>
                <Link to="/contact" data-testid="about-cta-contact" className="btn-outline">Contact Clinic</Link>
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
